import React, {
	useState,
	useEffect,
	useRef,
	forwardRef,
	useImperativeHandle,
	useCallback,
} from "react";
import {
	format,
	addHours,
	subHours,
	startOfDay,
	differenceInMinutes,
	parseISO,
	differenceInDays,
	isSameDay,
} from "date-fns";
import EventWheelItem from "./EventWheelItem";
import AllDayEventItem from "./AllDayEventItem";
import CreateEvent from "./CreateEvent";

// Constants for time slots
const HOURS_PER_VIEW = 9;
const MINUTES_PER_HOUR = 60;
const BUFFER_DAYS = 5;
const TOTAL_DAYS = 100;
const HOURS_BUFFER = TOTAL_DAYS * 24;

const EventWheel = forwardRef(
	(
		{
			events = [],
			allDayEvents = [],
			onDateChange,
			currentDate,
			onEventUpdate,
			onEventDelete,
			onEventCreate,
			isLoading = false,
		},
		ref
	) => {
		const [scrollOffset, setScrollOffset] = useState(0);
		const [isDragging, setIsDragging] = useState(false);
		const [startY, setStartY] = useState(0);
		const [currentTime, setCurrentTime] = useState(new Date());
		const [showCurrentTime, setShowCurrentTime] = useState(true);
		const [timeSlots, setTimeSlots] = useState([]);
		const [slotHeight, setSlotHeight] = useState(0);
		const [isUserInteracting, setIsUserInteracting] = useState(false);
		const [lastTouchY, setLastTouchY] = useState(0);
		const [initialTouchY, setInitialTouchY] = useState(0);
		const [momentum, setMomentum] = useState({ velocity: 0, timestamp: 0 });
		const momentumRef = useRef(null);
		const [isAnyPopoverOpen, setIsAnyPopoverOpen] = useState(false);
		const [showCreateEvent, setShowCreateEvent] = useState(false);
		const [createEventTime, setCreateEventTime] = useState(null);
		const anchorDateRef = useRef(new Date());
		const [isViewingCurrentTime, setIsViewingCurrentTime] = useState(true);

		const containerRef = useRef(null);
		const timeContainerRef = useRef(null);
		const selectedDateRef = useRef(new Date());
		const resizeObserverRef = useRef(null);
		const timeUpdateIntervalRef = useRef(null);
		const resizeTimeoutRef = useRef(null);
		const touchStartTimeRef = useRef({ current: 0, lastPosition: null });

		const calculateTimePosition = useCallback((date) => {
			if (!timeContainerRef.current || !containerRef.current) return 0;

			const containerHeight = timeContainerRef.current.offsetHeight;
			const newSlotHeight = containerHeight / HOURS_PER_VIEW;

			const midnight = startOfDay(date);
			const minutesSinceMidnight = differenceInMinutes(date, midnight);
			const hoursFromMidnight = minutesSinceMidnight / MINUTES_PER_HOUR;
			const timeOffset = hoursFromMidnight * newSlotHeight;

			const viewportCenter = containerHeight / 2;

			return -timeOffset + viewportCenter;
		}, []);

		const handlePopoverStateChange = useCallback(
			(isOpen) => {
				setIsAnyPopoverOpen(isOpen);
				if (!isOpen) {
					// When closing popover, ensure we're using the actual current time
					const now = new Date();
					selectedDateRef.current = now;
					setCurrentTime(now);
					if (showCurrentTime) {
						const newOffset = calculateTimePosition(now);
						setScrollOffset(newOffset);
						if (onDateChange) {
							onDateChange(now);
						}
					}
				}
			},
			[showCurrentTime, calculateTimePosition, onDateChange]
		);

		const calculateSlotHeight = useCallback(() => {
			if (!timeContainerRef.current) return 0;
			const containerHeight = timeContainerRef.current.offsetHeight;
			return containerHeight / HOURS_PER_VIEW;
		}, []);

		const getTimeFromPosition = useCallback(
			(clientY) => {
				if (!timeContainerRef.current) return null;

				const rect = timeContainerRef.current.getBoundingClientRect();
				const relativeY = clientY - rect.top;
				const containerHeight = timeContainerRef.current.offsetHeight;
				const viewCenter = containerHeight / 2;
				const offsetFromCenter = relativeY - viewCenter;

				// Calculate total offset from midnight
				const totalOffset = scrollOffset + -offsetFromCenter;
				const hourOffset = totalOffset / slotHeight - HOURS_PER_VIEW / 2;

				// Day calculation remains the same
				const daysPassed =
					hourOffset > 0
						? Math.ceil((hourOffset - 0.1) / 24)
						: Math.ceil(hourOffset / 24);

				const baseDate = startOfDay(currentDate);
				// Reverse the hourOffset for time calculation
				const clickedTime = addHours(baseDate, -hourOffset);

				// Round to nearest 15 minutes
				const minutes = clickedTime.getMinutes();
				const roundedMinutes = Math.round(minutes / 15) * 15;

				const roundedTime = new Date(clickedTime);
				roundedTime.setMinutes(roundedMinutes);
				roundedTime.setSeconds(0);
				roundedTime.setMilliseconds(0);

				return { time: roundedTime, daysPassed };
			},
			[slotHeight, currentDate, scrollOffset]
		);

		const handleDoubleClick = useCallback(
			(e) => {
				if (isAnyPopoverOpen) return;

				const result = getTimeFromPosition(e.clientY);
				if (!result) return;

				const { time: clickTime, daysPassed } = result;

				// Use anchorDateRef instead of new Date() or currentDate
				let eventDate = new Date(anchorDateRef.current);
				eventDate.setDate(eventDate.getDate() - daysPassed);

				const hour = clickTime.getHours();
				const minute = clickTime.getMinutes();

				// Create start time with correct date
				const startTime = new Date(eventDate);
				startTime.setHours(hour, minute, 0, 0);

				// Create end time (30 minutes later)
				const endTime = new Date(eventDate);
				if (minute + 30 >= 60) {
					endTime.setHours(hour + 1, minute + 30 - 60, 0, 0);
				} else {
					endTime.setHours(hour, minute + 30, 0, 0);
				}

				setCreateEventTime({
					start: startTime,
					end: endTime,
					date: eventDate,
				});
				setShowCreateEvent(true);
			},
			[isAnyPopoverOpen, getTimeFromPosition]
		);

		const initializeTimeSlots = useCallback((baseTime) => {
			const slots = [];
			const baseDay = startOfDay(baseTime);

			for (let i = -HOURS_BUFFER; i <= HOURS_BUFFER; i++) {
				const time = addHours(baseDay, i);
				slots.push({
					time,
					label: format(time, "h:mm a"),
					position: i,
					date: startOfDay(time),
				});
			}
			return slots;
		}, []);

		const getIconLink = (url) => {
			if (url.includes("docs.google.com/document"))
				return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.document";
			if (url.includes("docs.google.com/spreadsheets"))
				return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.spreadsheet";
			if (url.includes("docs.google.com/presentation"))
				return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.presentation";
			if (url.includes("docs.google.com/forms"))
				return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.form";

			// Default icon for other files
			return "https://drive-thirdparty.googleusercontent.com/16/type/text/uri-list";
		};

		const handleCreateEvent = useCallback(
			async (formData) => {
				if (!onEventCreate) return;
				console.log("Create event form data:", formData);
				try {
					const newEvent = {
						summary: formData.summary,
						description: formData.description || "",
						start: formData.start,
						end: formData.end,
						timezone: formData.timezone,
						allDay: formData.isAllDay,
						attendees: formData.attendees || [],
						location: formData.location,
						conferencing: formData.conferencing,
						// Add attachments data
						attachments: formData.attachments?.map((doc) => ({
							fileUrl: doc.fileUrl,
							title: doc.title,
							mimeType: doc.mimeType,
							iconLink: getIconLink(doc.fileUrl),
						})),
						supportsAttachments: true,
						recurrence: formData.recurrence,
						reminders: formData.reminders
							? {
									useDefault: false,
									overrides: Array.isArray(formData.reminders)
										? formData.reminders.map((minutes) => ({
												method: "popup",
												minutes: parseInt(minutes),
										  }))
										: formData.reminders.overrides || [],
							  }
							: {
									useDefault: false,
									overrides: [],
							  },
						visibility: formData.visibility,
						status: formData.status,
						sendUpdates: formData.sendUpdates,
						guestsCanModify: formData.guestPermissions?.canModify || false,
						guestsCanInviteOthers:
							formData.guestPermissions?.canInviteOthers || true,
						guestsCanSeeOtherGuests:
							formData.guestPermissions?.canSeeGuestList || true,
					};
					console.log("New event data:", newEvent);

					await onEventCreate(newEvent);
					setShowCreateEvent(false);
				} catch (error) {
					console.error("Failed to create event:", error);
					throw error;
				}
			},
			[onEventCreate]
		);

		useEffect(() => {
			if (!containerRef.current) return;

			const scrollableArea = containerRef.current.querySelector(
				".absolute.inset-0.cursor-grab"
			);
			if (!scrollableArea) return;

			scrollableArea.addEventListener("dblclick", handleDoubleClick);

			return () => {
				scrollableArea.removeEventListener("dblclick", handleDoubleClick);
			};
		}, [handleDoubleClick]);

		const handleResize = useCallback(() => {
			if (!timeContainerRef.current) return;

			const newSlotHeight = calculateSlotHeight();
			setSlotHeight(newSlotHeight);

			if (showCurrentTime) {
				const newOffset = calculateTimePosition(new Date());
				setScrollOffset(newOffset);
			}
		}, [calculateSlotHeight, calculateTimePosition, showCurrentTime]);

		useEffect(() => {
			// Initial setup
			handleResize();

			// Add resize listener
			const debouncedResize = () => {
				clearTimeout(resizeTimeoutRef.current);
				resizeTimeoutRef.current = setTimeout(handleResize, 100);
			};

			window.addEventListener("resize", debouncedResize);

			return () => {
				window.removeEventListener("resize", debouncedResize);
				if (resizeTimeoutRef.current) {
					clearTimeout(resizeTimeoutRef.current);
				}
			};
		}, [handleResize]);

		useEffect(() => {
			const handleResizeWithDebounce = () => {
				if (resizeTimeoutRef.current) {
					clearTimeout(resizeTimeoutRef.current);
				}

				resizeTimeoutRef.current = setTimeout(() => {
					handleResize();
				}, 100); // Debounce resize events
			};

			resizeObserverRef.current = new ResizeObserver(handleResizeWithDebounce);

			if (timeContainerRef.current) {
				resizeObserverRef.current.observe(timeContainerRef.current);
			}

			// Also listen for window resize events
			window.addEventListener("resize", handleResizeWithDebounce);

			return () => {
				if (resizeObserverRef.current) {
					resizeObserverRef.current.disconnect();
				}
				window.removeEventListener("resize", handleResizeWithDebounce);
				if (resizeTimeoutRef.current) {
					clearTimeout(resizeTimeoutRef.current);
				}
			};
		}, [handleResize]);

		const updateCurrentView = useCallback(
			(offset) => {
				if (!timeContainerRef.current) return;

				const newSlotHeight = calculateSlotHeight();
				const hourOffset = -offset / newSlotHeight;
				const newDate = addHours(
					startOfDay(selectedDateRef.current),
					hourOffset
				);

				if (onDateChange) {
					onDateChange(newDate);
				}
			},
			[onDateChange, calculateSlotHeight]
		);

		const scrollToDate = useCallback(
			(date) => {
				if (!timeContainerRef.current) return;

				const targetDate = new Date(date);
				selectedDateRef.current = targetDate;
				anchorDateRef.current = targetDate;

				// Check if the target date is "now"
				const now = new Date();
				const isCurrentTime = Math.abs(targetDate - now) < 60000; // Within 1 minute
				setIsViewingCurrentTime(isCurrentTime);

				const newOffset = calculateTimePosition(targetDate);

				requestAnimationFrame(() => {
					setScrollOffset(newOffset);
					setShowCurrentTime(isCurrentTime);
					setCurrentTime(targetDate);
					setTimeSlots(initializeTimeSlots(targetDate));

					if (onDateChange) {
						onDateChange(targetDate);
					}
				});
			},
			[onDateChange, initializeTimeSlots, calculateTimePosition]
		);
		const loadMoreSlots = useCallback((direction) => {
			setTimeSlots((currentSlots) => {
				if (direction === "future") {
					const lastSlot = currentSlots[currentSlots.length - 1];
					const newSlots = Array.from({ length: HOURS_BUFFER }, (_, i) => {
						const time = addHours(lastSlot.time, i + 1);
						return {
							time,
							label: format(time, "h:mm a"),
							position: lastSlot.position + i + 1,
							date: startOfDay(time),
						};
					});
					const combinedSlots = [...currentSlots, ...newSlots];
					return combinedSlots.slice(-HOURS_BUFFER * 3);
				} else {
					const firstSlot = currentSlots[0];
					const newSlots = Array.from({ length: HOURS_BUFFER }, (_, i) => {
						const time = subHours(firstSlot.time, i + 1);
						return {
							time,
							label: format(time, "h:mm a"),
							position: firstSlot.position - (i + 1),
							date: startOfDay(time),
						};
					}).reverse();
					const combinedSlots = [...newSlots, ...currentSlots];
					return combinedSlots.slice(0, HOURS_BUFFER * 3);
				}
			});
		}, []);

		const handleDragStart = useCallback(
			(e) => {
				if (isAnyPopoverOpen) return;
				e.preventDefault();
				setIsDragging(true);
				setStartY(e.clientY);
				setIsUserInteracting(true);
			},
			[isAnyPopoverOpen]
		);

		const handleDragMove = useCallback(
			(e) => {
				if (!isDragging) return;
				e.preventDefault();
				setShowCurrentTime(false);
				const deltaY = e.clientY - startY;
				const newOffset = scrollOffset + deltaY;

				setScrollOffset(newOffset);
				updateCurrentView(newOffset);
				setStartY(e.clientY);

				// Load more slots if needed
				const currentDate = addHours(
					startOfDay(selectedDateRef.current),
					-newOffset / slotHeight
				);

				const firstSlot = timeSlots[0];
				const lastSlot = timeSlots[timeSlots.length - 1];

				if (firstSlot && lastSlot) {
					const daysToStart = differenceInDays(currentDate, firstSlot.time);
					const daysToEnd = differenceInDays(lastSlot.time, currentDate);

					if (daysToStart < BUFFER_DAYS) {
						loadMoreSlots("past");
					}
					if (daysToEnd < BUFFER_DAYS) {
						loadMoreSlots("future");
					}
				}
			},
			[
				isDragging,
				startY,
				scrollOffset,
				slotHeight,
				timeSlots,
				loadMoreSlots,
				updateCurrentView,
			]
		);

		const handleDragEnd = useCallback(() => {
			setIsDragging(false);
			// Add a delay before resetting user interaction to allow any animations to complete
			setTimeout(() => {
				setIsUserInteracting(false);
			}, 1000);

			// Check if we're near current time after drag
			const now = new Date();
			const currentViewTime = addHours(
				startOfDay(selectedDateRef.current),
				-scrollOffset / slotHeight
			);
			const isNearCurrentTime = Math.abs(currentViewTime - now) < 60000; // Within 1 minute
			setIsViewingCurrentTime(isNearCurrentTime);
		}, [scrollOffset, slotHeight]);

		const handleTouchStart = useCallback(
			(e) => {
				if (isAnyPopoverOpen) return;
				e.preventDefault();
				const touch = e.touches[0];
				setIsDragging(true);
				setStartY(touch.clientY);
				setLastTouchY(touch.clientY);
				setInitialTouchY(touch.clientY);
				setIsUserInteracting(true);

				// Double tap detection
				const currentTime = Date.now();
				const tapPosition = { x: touch.clientX, y: touch.clientY };

				if (
					touchStartTimeRef.current &&
					currentTime - touchStartTimeRef.current < 300
				) {
					// Check if the tap is in roughly the same position (within 30px)
					const lastTap = touchStartTimeRef.lastPosition || { x: 0, y: 0 };
					const xDiff = Math.abs(lastTap.x - tapPosition.x);
					const yDiff = Math.abs(lastTap.y - tapPosition.y);

					if (xDiff < 30 && yDiff < 30) {
						// This is a double tap - trigger the double click handler
						handleDoubleClick({ clientY: touch.clientY });
						// Reset the tap timer to prevent multiple double tap detections
						touchStartTimeRef.current = 0;
						return;
					}
				}

				// Store the tap time and position for next comparison
				touchStartTimeRef.current = currentTime;
				touchStartTimeRef.lastPosition = tapPosition;

				// Clear any ongoing momentum
				if (momentumRef.current) {
					cancelAnimationFrame(momentumRef.current);
					momentumRef.current = null;
				}
			},
			[isAnyPopoverOpen, handleDoubleClick]
		);

		const handleTouchMove = useCallback(
			(e) => {
				if (!isDragging) return;
				e.preventDefault();

				const touch = e.touches[0];
				// If significant movement occurred, cancel potential double tap
				const xDiff = Math.abs(
					touch.clientX - (touchStartTimeRef.lastPosition?.x || 0)
				);
				const yDiff = Math.abs(
					touch.clientY - (touchStartTimeRef.lastPosition?.y || 0)
				);

				if (xDiff > 10 || yDiff > 10) {
					touchStartTimeRef.current = 0;
				}

				const deltaY = touch.clientY - lastTouchY;
				const newOffset = scrollOffset + deltaY;

				setScrollOffset(newOffset);
				setShowCurrentTime(false);
				updateCurrentView(newOffset);
				setLastTouchY(touch.clientY);

				// Calculate and store velocity for momentum
				const now = Date.now();
				const velocity = deltaY / (now - momentum.timestamp);
				setMomentum({ velocity, timestamp: now });

				// Load more slots if needed
				const currentDate = addHours(
					startOfDay(selectedDateRef.current),
					-newOffset / slotHeight
				);

				const firstSlot = timeSlots[0];
				const lastSlot = timeSlots[timeSlots.length - 1];

				if (firstSlot && lastSlot) {
					const daysToStart = differenceInDays(currentDate, firstSlot.time);
					const daysToEnd = differenceInDays(lastSlot.time, currentDate);

					if (daysToStart < BUFFER_DAYS) {
						loadMoreSlots("past");
					}
					if (daysToEnd < BUFFER_DAYS) {
						loadMoreSlots("future");
					}
				}
			},
			[
				isDragging,
				lastTouchY,
				scrollOffset,
				momentum.timestamp,
				slotHeight,
				timeSlots,
				loadMoreSlots,
				updateCurrentView,
			]
		);

		const applyMomentum = useCallback(() => {
			const FRICTION = 0.95;
			const MIN_VELOCITY = 0.1;

			setMomentum((prevMomentum) => {
				const newVelocity = prevMomentum.velocity * FRICTION;

				if (Math.abs(newVelocity) < MIN_VELOCITY) {
					if (momentumRef.current) {
						cancelAnimationFrame(momentumRef.current);
						momentumRef.current = null;
					}
					setIsUserInteracting(false);
					return prevMomentum;
				}

				setScrollOffset((prevOffset) => {
					const newOffset = prevOffset + newVelocity * 16; // 16ms is roughly one frame
					updateCurrentView(newOffset);
					return newOffset;
				});

				momentumRef.current = requestAnimationFrame(applyMomentum);
				return { ...prevMomentum, velocity: newVelocity };
			});
		}, [updateCurrentView]);

		const handleTouchEnd = useCallback(
			(e) => {
				if (!isDragging) return;
				setIsDragging(false);

				const endTime = Date.now();
				const touchDuration = endTime - touchStartTimeRef.current;
				const totalDeltaY = lastTouchY - initialTouchY;

				// Only apply momentum if this wasn't a potential double tap
				if (touchDuration > 300 && Math.abs(totalDeltaY) > 30) {
					const velocity = totalDeltaY / touchDuration;
					setMomentum({ velocity, timestamp: endTime });
					momentumRef.current = requestAnimationFrame(applyMomentum);
				} else {
					setIsUserInteracting(false);
				}
			},
			[isDragging, lastTouchY, initialTouchY, applyMomentum]
		);

		const handleWheel = useCallback(
			(e) => {
				if (isAnyPopoverOpen) return;
				e.preventDefault();
				e.stopPropagation();
				setIsUserInteracting(true);

				requestAnimationFrame(() => {
					const deltaY = -e.deltaY * 0.8;
					const newOffset = scrollOffset + deltaY;

					setScrollOffset(newOffset);
					setShowCurrentTime(false);

					const currentDate = addHours(
						startOfDay(selectedDateRef.current),
						-newOffset / slotHeight
					);

					const firstSlot = timeSlots[0];
					const lastSlot = timeSlots[timeSlots.length - 1];

					if (firstSlot && lastSlot) {
						const daysToStart = differenceInDays(currentDate, firstSlot.time);
						const daysToEnd = differenceInDays(lastSlot.time, currentDate);

						if (daysToStart < BUFFER_DAYS) {
							loadMoreSlots("past");
						}
						if (daysToEnd < BUFFER_DAYS) {
							loadMoreSlots("future");
						}
					}

					updateCurrentView(newOffset);

					// Reset user interaction after a delay
					clearTimeout(timeUpdateIntervalRef.current);
					timeUpdateIntervalRef.current = setTimeout(() => {
						setIsUserInteracting(false);
					}, 1000);
				});
			},
			[
				isAnyPopoverOpen,
				scrollOffset,
				slotHeight,
				timeSlots,
				loadMoreSlots,
				updateCurrentView,
			]
		);
		const [, forceUpdate] = useState({});
		const forceRerender = useCallback(() => {
			forceUpdate({});
		}, []);

		useEffect(() => {
			let timeoutId;

			const scheduleNextUpdate = () => {
				const now = new Date();
				const msToNextMinute =
					60000 - (now.getSeconds() * 1000 + now.getMilliseconds());

				timeoutId = setTimeout(() => {
					const now = new Date();

					// Only update if we're viewing current time
					if (!isUserInteracting && isViewingCurrentTime) {
						selectedDateRef.current = now;
						setCurrentTime(now); // Keep this for the time indicator
						const newOffset = calculateTimePosition(now);
						setScrollOffset(newOffset);

						if (onDateChange) {
							onDateChange(now);
						}

						// Force a re-render to update the event positions
						forceRerender();
					}

					// Schedule the next update
					scheduleNextUpdate();
				}, msToNextMinute);
			};

			// Only start the timer if we're viewing current time
			if (isViewingCurrentTime) {
				scheduleNextUpdate();
			}

			return () => {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			};
		}, [
			isUserInteracting,
			calculateTimePosition,
			onDateChange,
			isViewingCurrentTime,
			forceRerender, // Add this to dependencies
		]);

		useEffect(() => {
			if (!containerRef.current) return;

			const scrollableArea = containerRef.current.querySelector(
				".absolute.inset-0.cursor-grab"
			);
			if (!scrollableArea) return;

			let isScrolling = false;
			let frameId = null;

			const wheelHandler = (e) => {
				if (frameId) {
					cancelAnimationFrame(frameId);
				}

				if (!isScrolling) {
					isScrolling = true;
				}

				frameId = requestAnimationFrame(() => {
					handleWheel(e);
					isScrolling = false;
				});
			};

			scrollableArea.addEventListener("wheel", wheelHandler, {
				passive: false,
			});

			return () => {
				if (frameId) {
					cancelAnimationFrame(frameId);
				}
				scrollableArea.removeEventListener("wheel", wheelHandler);
			};
		}, [handleWheel]);

		const resetScroll = useCallback(() => {
			if (!timeContainerRef.current) return;

			const now = new Date();
			anchorDateRef.current = now;
			selectedDateRef.current = now;
			setCurrentTime(now);
			setTimeSlots(initializeTimeSlots(now));
			setIsViewingCurrentTime(true);

			const newOffset = calculateTimePosition(now);
			setScrollOffset(newOffset);
			setShowCurrentTime(true);

			if (onDateChange) {
				onDateChange(now);
			}
		}, [onDateChange, initializeTimeSlots, calculateTimePosition]);

		useImperativeHandle(
			ref,
			() => ({
				resetScroll,
				scrollToDate,
			}),
			[resetScroll, scrollToDate]
		);

		useEffect(() => {
			const initializeWheel = () => {
				if (!timeContainerRef.current) return;

				const now = new Date();
				selectedDateRef.current = now;
				setCurrentTime(now);

				// Initialize time slots
				const initialTimeSlots = initializeTimeSlots(now);
				setTimeSlots(initialTimeSlots);

				const initialOffset = calculateTimePosition(now);
				setScrollOffset(initialOffset);
				setShowCurrentTime(true);

				if (onDateChange) {
					onDateChange(now);
				}
			};

			initializeWheel();
		}, [onDateChange, initializeTimeSlots, calculateTimePosition]);

		useEffect(() => {
			if (
				!timeContainerRef.current ||
				!containerRef.current ||
				!showCurrentTime ||
				isDragging
			)
				return;

			// Only update position if we're showing current time and not interacting
			const timeoutId = setTimeout(() => {
				if (!isDragging && showCurrentTime) {
					const newOffset = calculateTimePosition(selectedDateRef.current);
					setScrollOffset(newOffset);
				}
			}, 300); // Longer delay to avoid interference

			return () => clearTimeout(timeoutId);
		}, [
			allDayEvents.length,
			isDragging,
			showCurrentTime,
			calculateTimePosition,
		]);

		const renderAllDayEvents = useCallback(() => {
			if (!allDayEvents || allDayEvents.length === 0) return null;

			const selectedDate = startOfDay(currentDate);

			const visibleAllDayEvents = allDayEvents.filter((event) => {
				const eventStart =
					event.start instanceof Date ? event.start : parseISO(event.start);
				return isSameDay(startOfDay(eventStart), selectedDate);
			});

			if (visibleAllDayEvents.length === 0) return null;

			return (
				<div className="absolute top-0 left-0 right-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-blue-500/40 shadow-sm">
					<div className="px-3 pt-2">
						<span className="text-xs font-medium text-gray-500 dark:text-gray-400">
							All-day
						</span>
					</div>
					<div className="space-y-1 pb-2">
						{visibleAllDayEvents.map((event) => (
							<AllDayEventItem key={event.id} event={event} compact={true} />
						))}
					</div>
				</div>
			);
		}, [allDayEvents, currentDate]);

		const getAllDayEventsHeight = useCallback(() => {
			const selectedDate = startOfDay(selectedDateRef.current);
			const visibleAllDayEvents = allDayEvents.filter((event) => {
				const eventStart =
					event.start instanceof Date ? event.start : parseISO(event.start);
				return isSameDay(startOfDay(eventStart), selectedDate);
			});

			if (visibleAllDayEvents.length === 0) return 0;
			// Height calculation: padding + header + (number of events * event height) + bottom padding
			return 8 + 20 + visibleAllDayEvents.length * 32 + 8; // pt-2 + header + events + pb-2
		}, [allDayEvents]);

		function dayDifference(startDay, endDay) {
			// Convert both dates to start of day to ignore time components
			const start = startOfDay(startDay);
			const end = startOfDay(endDay);

			// Calculate difference in milliseconds
			const diffInMs = start.getTime() - end.getTime();

			// Convert to days (1 day = 24 * 60 * 60 * 1000 milliseconds)
			const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

			return diffInDays;
		}

		const handleEventUpdate = useCallback(
			async (updatedEvent) => {
				try {
					if (onEventUpdate) {
						await onEventUpdate(updatedEvent);
					}
				} catch (error) {
					console.error("Failed to update event:", error);
					throw error; // Propagate error to handle in UI
				}
			},
			[onEventUpdate]
		);

		const handleEventDelete = useCallback(
			async (eventId) => {
				try {
					if (onEventDelete) {
						await onEventDelete(eventId);
					}
				} catch (error) {
					console.error("Failed to delete event:", error);
					throw error; // Propagate error to handle in UI
				}
			},
			[onEventDelete]
		);

		const renderTimeSlots = useCallback(() => {
			if (!timeContainerRef.current || slotHeight === 0) return null;
			const containerHeight = timeContainerRef.current.offsetHeight;
			const buffer = containerHeight * 3;

			// Filter visible time slots
			const visibleTimeSlots = timeSlots.filter((slot) => {
				const position = slot.position * slotHeight + scrollOffset;
				return Math.abs(position) <= containerHeight + buffer;
			});

			// Use currentViewDate instead of current time for reference
			const referenceDate = startOfDay(selectedDateRef.current);

			// Calculate visible range using scroll position
			const hourOffset = -scrollOffset / slotHeight;
			const centerDate = addHours(referenceDate, hourOffset);

			// Expand visible range for better transitions
			const visibleStart = subHours(centerDate, HOURS_PER_VIEW);
			const visibleEnd = addHours(centerDate, HOURS_PER_VIEW);

			// Filter events within expanded range
			const visibleEvents = events.filter((event) => {
				const eventStart =
					event.start instanceof Date ? event.start : parseISO(event.start);
				const eventEnd =
					event.end instanceof Date ? event.end : parseISO(event.end);

				return (
					(eventStart <= visibleEnd && eventEnd >= visibleStart) ||
					isSameDay(startOfDay(eventStart), startOfDay(centerDate))
				);
			});

			return (
				<>
					{visibleTimeSlots.map((slot) => {
						const position = slot.position * slotHeight + scrollOffset;
						const isHour = slot.time.getMinutes() === 0;
						const hour = slot.time.getHours();
						const period = hour >= 12 ? "PM" : "AM";
						const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

						return (
							<div
								key={slot.time.getTime()}
								style={{
									height: `${slotHeight}px`,
									transform: `translateY(${position}px)`,
									position: "absolute",
									left: 0,
									right: 0,
									transition: "none",
									willChange: "transform",
								}}
								className="border-b border-blue-500/40 relative"
							>
								{isHour && (
									<div className="absolute top-0 left-0 right-0 px-3 -translate-y-1/2 flex items-center">
										<div className="flex items-baseline space-x-1">
											<span className="text-gray-900 dark:text-gray-200 text-xs font-medium">
												{displayHour}
											</span>
											<span className="text-gray-500 dark:text-gray-400 text-[10px] font-medium">
												{period}
											</span>
										</div>
									</div>
								)}

								{/* 15-minute marker */}
								<div
									className="absolute left-0 right-0 px-6"
									style={{ top: `${slotHeight / 4}px` }}
								>
									<div className="border-t border-blue-400/30 w-1/6" />
								</div>

								{/* 30-minute marker */}
								<div
									className="absolute left-0 right-0 px-6"
									style={{ top: `${slotHeight / 2}px` }}
								>
									<div className="border-t border-blue-400/60 w-1/4" />
								</div>

								{/* 45-minute marker */}
								<div
									className="absolute left-0 right-0 px-6"
									style={{ top: `${(slotHeight * 3) / 4}px` }}
								>
									<div className="border-t border-blue-400/30 w-1/6" />
								</div>
							</div>
						);
					})}

					{visibleEvents.map((event) => {
						const eventStart =
							event.start instanceof Date ? event.start : parseISO(event.start);
						const referenceDate = selectedDateRef.current;
						const daysDiff = dayDifference(
							eventStart,
							startOfDay(referenceDate)
						);
						const hoursFromMidnight =
							differenceInMinutes(eventStart, startOfDay(eventStart)) / 60;
						const position = daysDiff * 24 + hoursFromMidnight;
						return (
							<EventWheelItem
								key={event.id}
								event={event}
								containerHeight={containerHeight}
								slotHeight={slotHeight}
								scrollOffset={scrollOffset}
								isDragging={isDragging}
								position={position}
								compact={true}
								onPopoverStateChange={handlePopoverStateChange}
								onUpdateEvent={handleEventUpdate}
								onDeleteEvent={handleEventDelete}
							/>
						);
					})}
				</>
			);
		}, [
			timeSlots,
			scrollOffset,
			isDragging,
			events,
			slotHeight,
			handlePopoverStateChange,
			handleEventUpdate,
			handleEventDelete,
			selectedDateRef.current,
		]);

		return (
			<div
				className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 min-w-[180px] relative"
				ref={containerRef}
			>
				{isLoading && (
					<div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-50 flex items-center justify-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
					</div>
				)}

				<div
					className="relative flex-1 overflow-hidden"
					ref={timeContainerRef}
					style={{
						paddingTop: `${getAllDayEventsHeight()}px`,
					}}
				>
					{renderAllDayEvents()}
					<div
						className={`absolute inset-0 select-none ${
							isAnyPopoverOpen ? "cursor-default" : "cursor-grab"
						}`}
						onMouseDown={handleDragStart}
						onMouseMove={handleDragMove}
						onMouseUp={handleDragEnd}
						onMouseLeave={handleDragEnd}
						onTouchStart={handleTouchStart}
						onTouchMove={handleTouchMove}
						onTouchEnd={handleTouchEnd}
						onTouchCancel={handleTouchEnd}
						style={{
							touchAction: "none",
							paddingTop: `${getAllDayEventsHeight()}px`,
						}}
					>
						<div className="absolute inset-0">{renderTimeSlots()}</div>
					</div>

					{showCurrentTime && (
						<div
							className="absolute left-0 right-0 z-50 pointer-events-none"
							style={{
								top: "50%",
								transform: "translateY(-50%)",
							}}
						>
							<div className="px-2">
								<div className="relative flex items-center">
									<div className="h-0.5 flex-1 bg-blue-500" />
									<div className="ml-1 bg-blue-500 shadow-md text-white px-1.5 py-0.5 rounded text-xs whitespace-nowrap">
										{format(currentTime, "h:mm a")}
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
				<CreateEvent
					isOpen={showCreateEvent}
					onClose={() => setShowCreateEvent(false)}
					onCreateEvent={handleCreateEvent}
					initialTime={createEventTime}
				/>
			</div>
		);
	}
);

export default EventWheel;
